/**
 * Configuration Validation Utilities
 *
 * Compares expected configuration values against actual device responses.
 * Handles simple values (numbers, strings) and structured values (objects like IP/port).
 */

export interface ConfigMismatch {
  parameter: string;
  expected: unknown;
  received: unknown;
}

export interface ValidationResult {
  isValid: boolean;
  mismatches: ConfigMismatch[];
}

/**
 * Normalize a value for comparison.
 * Handles different types that should be considered equal:
 * - Numbers as strings vs actual numbers
 * - Objects with different structures but same data
 */
function normalizeValue(value: unknown): unknown {
  if (value === null || value === undefined) {
    return value;
  }

  // If it's a string that looks like a number, convert to number
  if (typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value)) {
    return Number(value);
  }

  // If it's an object, try to extract meaningful values
  if (typeof value === 'object' && !Array.isArray(value)) {
    const entries = Object.entries(value as Record<string, unknown>).filter(
      ([, v]) => v !== null && v !== undefined,
    );

    // For simple objects with a single value, return that value
    if (entries.length === 1) {
      return entries[0][1];
    }

    // For complex objects, return a normalized version
    const normalized: Record<string, unknown> = {};
    for (const [key, val] of entries) {
      normalized[key] = normalizeValue(val);
    }
    return normalized;
  }

  return value;
}

/**
 * Deep equality check for objects that ignores key order.
 * Recursively compares all properties.
 */
function deepEqual(a: unknown, b: unknown): boolean {
  // Handle primitives and null/undefined
  if (a === null || a === undefined) {
    return b === null || b === undefined;
  }
  if (b === null || b === undefined) {
    return false;
  }

  // Handle arrays
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((item, index) => deepEqual(item, b[index]));
  }
  if (Array.isArray(a) || Array.isArray(b)) {
    return false;
  }

  // Handle objects
  if (typeof a === 'object' && typeof b === 'object') {
    const keysA = Object.keys(a as Record<string, unknown>).sort();
    const keysB = Object.keys(b as Record<string, unknown>).sort();

    if (keysA.length !== keysB.length) return false;
    if (keysA.some((key, i) => key !== keysB[i])) return false;

    return keysA.every((key) =>
      deepEqual((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key]),
    );
  }

  // Handle primitives - use normalizeValue to handle type conversions
  const normA = normalizeValue(a);
  const normB = normalizeValue(b);

  if (typeof normA === 'string' && typeof normB === 'string') {
    return normA.trim() === normB.trim();
  }
  if (typeof normA === 'number' && typeof normB === 'number') {
    return Math.abs(normA - normB) < Number.EPSILON;
  }

  return normA === normB;
}

/**
 * Compare two values for equality, accounting for type conversions
 * and structural differences. Optionally aceita uma tolerância para comparação numérica.
 */
function valuesEqual(expected: unknown, received: unknown, tolerance = 0): boolean {
  const normalizedExpected = normalizeValue(expected);
  const normalizedReceived = normalizeValue(received);

  // Both null/undefined
  if (normalizedExpected === null || normalizedExpected === undefined) {
    return normalizedReceived === null || normalizedReceived === undefined;
  }

  if (normalizedReceived === null || normalizedReceived === undefined) {
    return false;
  }

  // String comparison (trim whitespace)
  if (typeof normalizedExpected === 'string' && typeof normalizedReceived === 'string') {
    return normalizedExpected.trim() === normalizedReceived.trim();
  }

  // Number comparison — aceita tolerância (útil p/ odometer que pode incrementar com ignição ligada)
  if (typeof normalizedExpected === 'number' && typeof normalizedReceived === 'number') {
    const effectiveTolerance = tolerance > 0 ? tolerance : Number.EPSILON;
    return Math.abs(normalizedExpected - normalizedReceived) <= effectiveTolerance;
  }

  // Object comparison - deep equality that ignores key order
  if (typeof normalizedExpected === 'object' && typeof normalizedReceived === 'object') {
    return deepEqual(normalizedExpected, normalizedReceived);
  }

  // Fallback to strict equality
  return normalizedExpected === normalizedReceived;
}

// Campos que toleram pequena diferença entre o valor escrito e o valor lido de volta.
// odometer pode incrementar entre o write e o CHECK quando a ignição está ligada.
const FIELD_TOLERANCE: Record<string, number> = {
  odometer: 20, // até 20 unidades de diferença é aceitável
};

/**
 * Validate configuration by comparing expected values with received values.
 *
 * @param expected - The expected configuration values (from profile)
 * @param received - The actual values received from the device
 * @returns ValidationResult with isValid flag and list of mismatches
 */
export function validateConfiguration(
  expected: Record<string, unknown>,
  received: Record<string, unknown>,
): ValidationResult {
  const mismatches: ConfigMismatch[] = [];

  // Get all keys from expected configuration
  const keys = Object.keys(expected);

  for (const key of keys) {
    const expectedValue = expected[key];
    const receivedValue = received[key];

    // Skip if expected value is empty object/null and received is undefined
    if (
      expectedValue !== null &&
      expectedValue !== undefined &&
      typeof expectedValue === 'object' &&
      !Array.isArray(expectedValue) &&
      Object.keys(expectedValue).length === 0
    ) {
      if (receivedValue === undefined || receivedValue === null) {
        continue;
      }
    }

    // Check if values match (com tolerância opcional definida por campo)
    const tolerance = FIELD_TOLERANCE[key] ?? 0;
    if (!valuesEqual(expectedValue, receivedValue, tolerance)) {
      mismatches.push({
        parameter: key,
        expected: expectedValue,
        received: receivedValue,
      });
    }
  }

  return {
    isValid: mismatches.length === 0,
    mismatches,
  };
}

/**
 * Format a mismatch for logging purposes.
 */
export function formatMismatch(mismatch: ConfigMismatch): string {
  const expectedStr =
    typeof mismatch.expected === 'object'
      ? JSON.stringify(mismatch.expected)
      : String(mismatch.expected ?? '--');
  const receivedStr =
    typeof mismatch.received === 'object'
      ? JSON.stringify(mismatch.received)
      : String(mismatch.received ?? '--');

  return `${mismatch.parameter}: expected ${expectedStr} received ${receivedStr}`;
}

/**
 * Create a detailed log message for validation results.
 */
export function createValidationLog(result: ValidationResult): string[] {
  const logs: string[] = [];

  if (result.isValid) {
    logs.push('✓ Configuração OK');
  } else {
    logs.push('✗ Configuração divergente');
    for (const mismatch of result.mismatches) {
      const expectedStr =
        typeof mismatch.expected === 'object'
          ? JSON.stringify(mismatch.expected)
          : String(mismatch.expected);
      const receivedStr =
        typeof mismatch.received === 'object'
          ? JSON.stringify(mismatch.received)
          : String(mismatch.received ?? 'N/A');

      logs.push(`  Parameter: ${mismatch.parameter}`);
      logs.push(`  Expected: ${expectedStr}`);
      logs.push(`  Received: ${receivedStr}`);
    }
  }

  return logs;
}
